/**
 * Task D4 — **the one place a job status becomes user-visible text or colour.**
 *
 * This file is deliberately thin. Every rule about how a job reads — that
 * `EXPIRED` is neutral rather than a failure, that a `SUBMITTED` job carrying
 * `VENDOR_JOB_ID_COLLISION` is progress, that `PARTIAL` is a warning and not an
 * error — already lives in `describeJob()` (ARCHITECTURE §04.5, shipped and
 * QA-verified in Phase 04). Re-deciding any of it here would be a second
 * implementation of the same judgement, and the failure mode of two
 * implementations is that one of them is wrong in one surface only.
 *
 * **Criterion 18 is a source scan: `describeJob(` may be called from this file
 * and nowhere else.** The list, the detail page, the drawer, the dashboard and
 * any toast all render through this component, so the seven statuses cannot
 * diverge between surfaces. If a new surface needs a job's tone, it composes
 * this; it does not reach past it.
 *
 * The only thing this component adds to `describeJob()` is DOM: a `Badge` whose
 * tone is a straight transliteration of `JobPresentation.tone`, the detail
 * sentence, and — when and only when `describeJob()` says so — the re-check
 * control. `data-job-tone` mirrors the presentation's tone onto the element so
 * a test can assert "EXPIRED is neutral" against the decision rather than
 * against a Tailwind class name that is Badge's business, not ours.
 */

import type { ClientJob } from '@rd/shared';
import type { ReactElement } from 'react';

import { Badge, type BadgeTone } from '../../components/Badge.js';
import { Button } from '../../components/Button.js';
import { describeJob, type JobTone } from '../../lib/useJob.js';

/**
 * A transliteration, not a decision.
 *
 * `JobTone` and `BadgeTone` happen to spell the five tones identically today.
 * The map exists anyway so that the day one of them gains a sixth value, this
 * is a compile error rather than a silent `undefined` className.
 */
const TONE_TO_BADGE: Record<JobTone, BadgeTone> = {
  progress: 'progress',
  success: 'success',
  warning: 'warning',
  error: 'error',
  neutral: 'neutral',
};

export interface JobStatusBadgeProps {
  job: ClientJob;
  /**
   * The error code recorded against the job, when there is one. Only
   * `VENDOR_JOB_ID_COLLISION` changes how a job reads, and that rule lives in
   * `describeJob()` — this is passed straight through.
   */
  errorCode?: string | null | undefined;
  /**
   * Bound to `useJob().refresh()` by the caller. Rendered only when
   * `describeJob()` asks for it, which is `EXPIRED` and the unknown-status
   * fallback. A caller that omits it gets no control, because a re-check button
   * that does nothing is worse than none.
   */
  onRecheck?: (() => void) | undefined;
  /** True while the re-check request is in flight. */
  rechecking?: boolean | undefined;
  /**
   * A dense context — a table cell, a dashboard row.
   *
   * The detail sentence is dropped **only when every item succeeded**, because
   * there the pill already says `Completed` and "All 1 completed successfully"
   * beside it is the same fact twice. It is kept for `PARTIAL`, `FAILED`,
   * `EXPIRED` and in-progress, where it carries what the pill cannot: which
   * count failed, how far along it is, what to do next.
   *
   * Same discipline as the toast rule and the `danger` colour — a signal
   * repeated everywhere stops being read.
   */
  compact?: boolean | undefined;
}

export function JobStatusBadge({
  job,
  errorCode,
  onRecheck,
  rechecking = false,
  compact = false,
}: JobStatusBadgeProps): ReactElement {
  const presentation = describeJob(job, errorCode);

  return (
    <div className="flex flex-col items-start gap-1" data-job-tone={presentation.tone}>
      <Badge tone={TONE_TO_BADGE[presentation.tone]}>{presentation.headline}</Badge>

      {compact && presentation.tone === 'success' ? null : (
        <p className="text-[length:var(--text-sm)] text-muted">{presentation.detail}</p>
      )}

      {presentation.recheck && onRecheck ? (
        <Button variant="secondary" loading={rechecking} onClick={onRecheck}>
          Re-check
        </Button>
      ) : null}
    </div>
  );
}
